<template>
  <gas-page-shell :breadcrumb="['设备管理', '设备列表']">
    <div class="stat-row">
      <div v-for="item in statCards" :key="item.key" class="stat-card">
        <div class="stat-icon" :style="{ background: item.bg, color: item.color }"><i :class="item.icon"></i></div>
        <div class="stat-body">
          <div class="stat-label">{{ item.label }}</div>
          <div class="stat-value" :style="{ color: item.valueColor || '#1d2129' }">{{ item.value }}</div>
        </div>
      </div>
    </div>

    <div class="search-section">
      <div class="section-title"><span class="title-bar"></span>检索条件</div>
      <el-form :inline="true" :model="query" size="small" label-width="96px" class="search-form search-form-layout">
        <el-form-item label="站点名称">
          <el-select v-model="query.siteName" clearable filterable placeholder="请选择站点" style="width:160px">
            <el-option v-for="s in siteOptions" :key="s" :label="s" :value="s" />
          </el-select>
        </el-form-item>
        <el-form-item label="设备名称">
          <el-input v-model="query.deviceName" clearable placeholder="请输入设备名称" style="width:160px" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="设备编号">
          <el-input v-model="query.deviceCode" clearable placeholder="设备唯一编号" style="width:160px" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="设备类型">
          <el-select v-model="query.terminalType" clearable placeholder="全部类型" style="width:160px">
            <el-option v-for="t in deviceTypeOptions" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>
        <el-form-item label="项目分类">
          <el-select v-model="query.projectCategoryId" clearable filterable placeholder="全部分类" style="width:160px">
            <el-option v-for="c in projectCategoryOptions" :key="c.categoryId" :label="c.categoryName" :value="c.categoryId" />
          </el-select>
        </el-form-item>
        <template v-if="advancedVisible">
          <el-form-item label="所属机构">
            <el-select v-model="query.orgId" clearable placeholder="全部机构" style="width:160px">
              <el-option v-for="o in orgOptions" :key="o.orgId" :label="o.orgName" :value="o.orgId" />
            </el-select>
          </el-form-item>
          <el-form-item label="型号">
            <el-select v-model="query.model" clearable filterable placeholder="全部型号" style="width:140px">
              <el-option v-for="m in modelOptions" :key="m" :label="m" :value="m" />
            </el-select>
          </el-form-item>
          <el-form-item label="启用状态">
            <el-select v-model="query.enabledStatus" clearable placeholder="全部状态" style="width:120px">
              <el-option label="已启用" value="0" />
              <el-option label="已停用" value="1" />
            </el-select>
          </el-form-item>
        </template>
        <div class="search-actions">
          <el-button type="text" class="btn-advanced" @click="advancedVisible = !advancedVisible">
            {{ advancedVisible ? '收起检索' : '高级检索' }}
            <i :class="advancedVisible ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </el-button>
          <el-button icon="el-icon-refresh-left" @click="resetSearch">重置</el-button>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
        </div>
      </el-form>
    </div>

    <div class="table-panel">
      <div class="table-toolbar">
        <div class="table-title-group">
          <span class="table-title">设备列表 <em>共 {{ total }} 条记录</em></span>
          <gas-table-toolbar
            :columns="tableColumnDefs"
            :visible-map.sync="tableColumnVisible"
            :selected-count="selectedIds.length"
            :show-batch-delete="tableCanBatchDelete"
            @batch-delete="batchRemove('所选设备将进入回收站，监控、报表、告警中不再显示。确认继续？')"
          />
        </div>
        <div class="toolbar-actions">
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate(emptyForm())">新增设备</el-button>
          <el-button size="small" class="btn-enable" icon="el-icon-circle-check" :disabled="!selectedIds.length" @click="batchStatus('0')">批量启用</el-button>
          <el-button size="small" class="btn-disable" icon="el-icon-remove-outline" :disabled="!selectedIds.length" @click="batchStatus('1')">批量停用</el-button>
          <el-button size="small" icon="el-icon-upload2" @click="triggerImport">导入</el-button>
          <el-button size="small" icon="el-icon-download" @click="exportData">导出</el-button>
          <el-button size="small" icon="el-icon-refresh" circle title="刷新" @click="refreshAll" />
          <input ref="importInput" type="file" accept=".csv" class="hidden-file" @change="onImportFile" />
        </div>
      </div>

      <el-table
        :data="list"
        v-loading="loading"
        border
        size="small"
        class="device-table"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="44" align="center" fixed="left" />
        <el-table-column label="序号" width="60" align="center" fixed="left" v-if="colVisible('rowNo')">
          <template slot-scope="{ $index }">{{ rowNo($index) }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('siteName')" prop="siteName" label="站点名称" min-width="120" show-overflow-tooltip />
        <el-table-column v-if="colVisible('deviceName')" label="设备名称/ID" min-width="150">
          <template slot-scope="{ row }">
            <div class="device-name link" @click="openDetailDrawer(row)">{{ row.deviceName }}</div>
            <div class="device-code">{{ row.deviceCode }}</div>
          </template>
        </el-table-column>
        <el-table-column v-if="colVisible('terminalType')" prop="terminalType" label="终端类型" width="96" show-overflow-tooltip />
        <el-table-column v-if="colVisible('projectCategoryName')" prop="projectCategoryName" label="项目分类" width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.projectCategoryName || '—' }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('model')" prop="model" label="型号" width="88" />
        <el-table-column v-if="colVisible('enabledStatus')" label="启用状态" width="88" align="center">
          <template slot-scope="{ row }"><gas-status-tag kind="enable" :value="row.enabledStatus" /></template>
        </el-table-column>
        <el-table-column v-if="colVisible('protocolType')" prop="protocolType" label="协议类型" width="96" show-overflow-tooltip />
        <el-table-column v-if="colVisible('customerName')" prop="customerName" label="使用客户" min-width="120" show-overflow-tooltip />
        <el-table-column v-if="colVisible('updateTime')" prop="updateTime" label="更新时间" width="150" />
        <el-table-column v-if="colVisible('meterTime')" prop="meterTime" label="采集时间" width="150">
          <template slot-scope="{ row }">{{ row.meterTime || '—' }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('hardwareCode')" prop="hardwareCode" label="硬件编码" width="110" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.hardwareCode || '—' }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('lngLat')" label="经度/纬度" width="130" align="center">
          <template slot-scope="{ row }">{{ formatLngLat(row) }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('terminalStatus')" label="终端状态" width="88" align="center">
          <template slot-scope="{ row }"><gas-status-tag kind="online" :value="row.terminalStatus" /></template>
        </el-table-column>
        <el-table-column v-if="colVisible('timeoutMinutes')" label="超时设定(分钟)" width="118" align="center">
          <template slot-scope="{ row }">{{ row.timeoutMinutes != null ? row.timeoutMinutes : '—' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" class="op-link" @click="pushDevice(row)">推送</el-button>
            <el-button type="text" size="small" class="op-link" @click="openEdit(row, emptyForm())">编辑</el-button>
            <el-button type="text" size="small" class="op-link" @click="openView(row)">查看</el-button>
            <el-button type="text" size="small" class="op-del" @click="removeRow(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :current-page="query.pageNum"
        :page-size="query.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="760px" @open="clearFormValidate">
      <el-form ref="deviceForm" :model="form" :rules="formRules" label-width="108px" size="small">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="站点名称" prop="siteName">
              <el-select v-model="form.siteName" clearable filterable placeholder="请选择站点" style="width:100%">
                <el-option v-for="s in siteOptions" :key="s" :label="s" :value="s" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="所属机构" prop="orgId">
              <el-select v-model="form.orgId" style="width:100%">
                <el-option v-for="o in orgOptions" :key="o.orgId" :label="o.orgName" :value="o.orgId" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12"><el-form-item label="设备名称" prop="deviceName"><el-input v-model="form.deviceName" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="设备编号" prop="deviceCode"><el-input v-model="form.deviceCode" :disabled="!!form.deviceId" /></el-form-item></el-col>
          <el-col :span="12">
            <el-form-item label="终端类型" prop="terminalType">
              <el-select v-model="form.terminalType" clearable filterable placeholder="请选择终端类型" style="width:100%" @change="onTerminalTypeChange">
                <el-option v-for="t in deviceTypeOptions" :key="t" :label="t" :value="t" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="项目分类" prop="projectCategoryId">
              <el-select
                v-model="form.projectCategoryId"
                clearable
                filterable
                placeholder="请选择项目分类"
                style="width:100%"
                @change="onProjectCategoryChange"
              >
                <el-option v-for="c in projectCategoryOptions" :key="c.categoryId" :label="c.categoryName" :value="c.categoryId" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12"><el-form-item label="型号" prop="model"><el-input v-model="form.model" /></el-form-item></el-col>
          <el-col :span="12">
            <el-form-item label="协议类型" prop="protocolType">
              <el-select
                v-model="form.protocolType"
                clearable
                filterable
                :disabled="!form.terminalType"
                :filter-method="filterProtocolOption"
                :placeholder="form.terminalType ? '搜索协议名称/编号/通讯方式等' : '请先选择终端类型'"
                style="width:100%"
                @visible-change="onProtocolDropdownVisible"
              >
                <el-option
                  v-for="p in filteredProtocolOptions"
                  :key="p.protocolId"
                  :label="protocolOptionLabel(p)"
                  :value="p.protocolName"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12"><el-form-item label="使用客户"><el-input v-model="form.customerName" /></el-form-item></el-col>
          <!-- 设备IP和设备端口已由协议配置，此处不再维护 -->
          <!-- <el-col :span="12"><el-form-item label="设备IP"><el-input v-model="form.ip" placeholder="如 192.168.1.10" /></el-form-item></el-col> -->
          <!-- <el-col :span="12"><el-form-item label="设备端口"><el-input v-model.number="form.port" placeholder="为空则使用协议端口" /></el-form-item></el-col> -->
          <el-col :span="12"><el-form-item label="硬件编码"><el-input v-model="form.hardwareCode" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="超时(分钟)"><el-input v-model.number="form.timeoutMinutes" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="经度"><el-input v-model="form.longitude" placeholder="如 121.473701" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="纬度"><el-input v-model="form.latitude" placeholder="如 31.230416" /></el-form-item></el-col>
          <el-col :span="24">
            <el-form-item label=" ">
              <el-button size="small" type="primary" plain icon="el-icon-map-location" @click="mapPickerVisible = true">地图选点</el-button>
              <span class="map-pick-hint">搜索地址或点击地图获取经纬度</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="启用状态">
              <el-select v-model="form.enabledStatus" style="width:100%">
                <el-option label="已启用" value="0" /><el-option label="已停用" value="1" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer"><el-button @click="dialogVisible = false">取消</el-button><el-button type="primary" @click="submitForm">保存</el-button></span>
    </el-dialog>

    <device-coord-picker
      :visible.sync="mapPickerVisible"
      :longitude="form.longitude"
      :latitude="form.latitude"
      :address-hint="form.siteName"
      @confirm="onMapPickConfirm"
    />

    <device-detail-drawer :visible.sync="drawerVisible" :device-id="drawerDeviceId" />
    <device-push-drawer :visible.sync="pushDrawerVisible" :device="pushDeviceRow" />

    <el-dialog title="设备详情" :visible.sync="viewVisible" width="680px">
      <el-descriptions v-if="viewRow" :column="2" border size="small">
        <el-descriptions-item label="站点名称">{{ viewRow.siteName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="所属机构">{{ orgName(viewRow.orgId) }}</el-descriptions-item>
        <el-descriptions-item label="设备名称">{{ viewRow.deviceName }}</el-descriptions-item>
        <el-descriptions-item label="设备编号">{{ viewRow.deviceCode }}</el-descriptions-item>
        <el-descriptions-item label="终端类型">{{ viewRow.terminalType || '—' }}</el-descriptions-item>
        <el-descriptions-item label="项目分类">{{ viewRow.projectCategoryName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="型号">{{ viewRow.model || '—' }}</el-descriptions-item>
        <el-descriptions-item label="启用状态"><gas-status-tag kind="enable" :value="viewRow.enabledStatus" /></el-descriptions-item>
        <el-descriptions-item label="终端状态"><gas-status-tag kind="online" :value="viewRow.terminalStatus" /></el-descriptions-item>
        <el-descriptions-item label="协议类型">{{ viewRow.protocolType || '—' }}</el-descriptions-item>
        <el-descriptions-item label="使用客户">{{ viewRow.customerName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="IP">{{ viewNetInfo.ip || '—' }}</el-descriptions-item>
        <el-descriptions-item label="端口">{{ viewNetInfo.port != null ? viewNetInfo.port : '—' }}</el-descriptions-item>
        <el-descriptions-item label="硬件编码">{{ viewRow.hardwareCode || '—' }}</el-descriptions-item>
        <el-descriptions-item label="超时(分钟)">{{ viewRow.timeoutMinutes != null ? viewRow.timeoutMinutes : '—' }}</el-descriptions-item>
        <el-descriptions-item label="经度/纬度" :span="2">{{ formatLngLat(viewRow) }}</el-descriptions-item>
        <el-descriptions-item label="更新时间">{{ viewRow.updateTime || '—' }}</el-descriptions-item>
        <el-descriptions-item label="采集时间">{{ viewRow.meterTime || '—' }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>

  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import DeviceDetailDrawer from '@/components/DeviceDetailDrawer.vue'
import DeviceCoordPicker from '@/components/DeviceCoordPicker.vue'
import DevicePushDrawer from '@/components/DevicePushDrawer.vue'
import gasCrudPage from '@/mixins/gasCrudPage'
import deviceTypeOptionsMixin from '@/mixins/deviceTypeOptions'
import siteOptionsMixin from '@/mixins/siteOptions'
import { DEVICE_LIST_COLUMNS } from '@/config/tableColumnPresets'
import { gasApi } from '@/api/gas'

const emptyForm = () => ({
  enabledStatus: '0',
  timeoutMinutes: 30,
  protocolType: '',
  projectCategoryId: null,
  projectCategoryName: '',
  ip: '',
  port: null
})

export default {
  components: { GasPageShell, DeviceDetailDrawer, DeviceCoordPicker, DevicePushDrawer },
  mixins: [gasCrudPage, deviceTypeOptionsMixin, siteOptionsMixin],
  data() {
    return {
      loadOrgOptions: true,
      idField: 'deviceId',
      listApi: gasApi.device.list,
      saveApi: gasApi.device.save,
      removeApi: gasApi.device.remove,
      batchRemoveApi: gasApi.device.batchRemove,
      statCards: [],
      modelOptions: [],
      projectCategoryOptions: [],
      protocolOptions: [],
      protocolFilterQuery: '',
      mapPickerVisible: false,
      advancedVisible: false,
      viewVisible: false,
      viewRow: null,
      drawerVisible: false,
      drawerDeviceId: null,
      pushDrawerVisible: false,
      pushDeviceRow: null,
      formRules: {
        siteName: [{ required: true, message: '请选择站点名称', trigger: 'change' }],
        orgId: [{ required: true, message: '请选择所属机构', trigger: 'change' }],
        deviceName: [{ required: true, message: '请输入设备名称', trigger: 'blur' }],
        deviceCode: [{ required: true, message: '请输入设备编号', trigger: 'blur' }],
        terminalType: [{ required: true, message: '请选择终端类型', trigger: 'change' }],
        projectCategoryId: [{ required: true, message: '请选择项目分类', trigger: 'change' }],
        model: [{ required: true, message: '请输入型号', trigger: 'blur' }],
        protocolType: [{ required: true, message: '请选择协议类型', trigger: 'change' }]
      },
      tableColumnStorageKey: 'device-list-v2',
      query: {
        pageNum: 1,
        pageSize: 10,
        deviceName: '',
        terminalType: '',
        projectCategoryId: null,
        deviceCode: '',
        siteName: '',
        orgId: null,
        model: '',
        enabledStatus: ''
      }
    }
  },
  computed: {
    viewNetInfo() {
      const row = this.viewRow || {}
      const key = (row.protocolType || '').trim()
      const p = (this.protocolOptions || []).find(o => o.protocolName === key || o.protocolCode === key) || null
      let ip = row.ip || ''
      let port = row.port != null ? row.port : null
      if (p) {
        if (p.port != null) port = p.port
        if (p.settingsJson) {
          try {
            const s = JSON.parse(p.settingsJson)
            // MQTT协议优先从 brokerUrl 解析服务器IP与端口
            if ((p.commType || '').toUpperCase() === 'MQTT') {
              const m = /^mqtt:\/\/([^/:?#]+)(?::(\d+))?/i.exec(s.brokerUrl || '')
              if (m) {
                if (m[1]) ip = m[1]
                if (m[2]) port = Number(m[2])
              }
            }
            // TCP/UDP协议读取协议高级配置中的服务器IP
            if (!ip && s.serverIp) ip = s.serverIp
          } catch (e) { /* settingsJson 解析失败时保持原值 */ }
        }
      }
      return { ip, port }
    },
    protocolsForTerminalType() {
      const type = ((this.form && this.form.terminalType) || '').trim()
      const all = this.protocolOptions || []
      const mqtt = all.filter(p => (p.applyMode || '').toLowerCase() === 'mqtt' || (p.commType || '').toUpperCase() === 'MQTT')
      if (!type) return mqtt
      const byType = all.filter(p => {
        const pt = (p.protocolType || '').trim()
        return pt === type || (type.includes('自定义') && pt === '自定义类型')
          || ((type === '市电远传' || type === '压力远传') && (pt === '市电压力远传' || pt === '市电远传压力'))
      })
      const ids = new Set(byType.map(p => p.protocolId))
      mqtt.forEach(p => {
        if (!ids.has(p.protocolId)) byType.push(p)
      })
      return byType
    },
    filteredProtocolOptions() {
      let list = this.protocolsForTerminalType
      const current = ((this.form && this.form.protocolType) || '').trim()
      if (current && !list.some(p => p.protocolName === current || p.protocolCode === current)) {
        const extra = (this.protocolOptions || []).find(p => p.protocolName === current || p.protocolCode === current)
        if (extra) list = list.concat([extra])
      }
      const q = (this.protocolFilterQuery || '').trim().toLowerCase()
      if (!q) return list
      return list.filter(p => this.protocolMatchText(p).includes(q))
    }
  },
  watch: {
    dialogVisible(val) {
      if (val) {
        this.loadProtocolOptions()
        this.loadSiteOptions().then(() => this.ensureSiteOption(this.form && this.form.siteName))
        this.protocolFilterQuery = ''
      }
    }
  },
  created() {
    this.initTableColumns(DEVICE_LIST_COLUMNS)
    this.loadStats()
    this.loadModelOptions()
    this.loadDeviceTypeOptions()
    this.loadSiteOptions()
    this.loadProjectCategoryOptions()
  },
  methods: {
    emptyForm,
    submitForm() {
      // 表单必填校验通过后才执行保存（doSubmitForm），校验失败只显示红字提示、不发请求
      this.$refs.deviceForm.validate(valid => {
        if (!valid) return
        this.doSubmitForm()
      })
    },
    clearFormValidate() {
      this.$nextTick(() => {
        if (this.$refs.deviceForm) this.$refs.deviceForm.clearValidate()
      })
    },
    buildQuery() {
      const p = { ...this.query }
      Object.keys(p).forEach(k => {
        if (p[k] === '' || p[k] === null) delete p[k]
      })
      return p
    },
    async loadData() {
      if (!this.listApi) return
      this.loading = true
      try {
        const res = await this.listApi(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    async loadStats() {
      const res = await gasApi.device.stats()
      const d = res.data || {}
      this.statCards = [
        { key: 'total', label: '设备总数', value: d.total || 0, icon: 'el-icon-s-tools', color: '#1677ff', bg: '#e8f3ff', valueColor: '#1677ff' },
        { key: 'enabled', label: '已启用', value: d.enabled || 0, icon: 'el-icon-circle-check', color: '#00b42a', bg: '#e8ffea', valueColor: '#00b42a' },
        { key: 'disabled', label: '已停用', value: d.disabled || 0, icon: 'el-icon-remove-outline', color: '#86909c', bg: '#f2f3f5', valueColor: '#4e5969' },
        { key: 'online', label: '在线设备', value: d.online || 0, icon: 'el-icon-connection', color: '#14c9c9', bg: '#e8fffb', valueColor: '#14c9c9' },
        { key: 'offline', label: '离线设备', value: d.offline || 0, icon: 'el-icon-warning-outline', color: '#f53f3f', bg: '#ffece8', valueColor: '#f53f3f' }
      ]
    },
    async loadModelOptions() {
      try {
        const res = await gasApi.device.modelOptions()
        this.modelOptions = res.data || []
      } catch (e) {
        this.modelOptions = []
      }
    },
    async loadProjectCategoryOptions() {
      try {
        const res = await gasApi.projectCategory.list({ pageNum: 1, pageSize: 500 })
        this.projectCategoryOptions = (res.data || {}).records || []
      } catch (e) {
        this.projectCategoryOptions = []
      }
    },
    onProjectCategoryChange(categoryId) {
      const hit = (this.projectCategoryOptions || []).find(c => String(c.categoryId) === String(categoryId))
      this.form.projectCategoryName = hit ? hit.categoryName : ''
    },
    onTerminalTypeChange() {
      this.protocolFilterQuery = ''
      const current = ((this.form && this.form.protocolType) || '').trim()
      if (!current) return
      const stillValid = this.protocolsForTerminalType.some(p => p.protocolName === current || p.protocolCode === current)
      if (!stillValid) this.form.protocolType = ''
    },
    findProjectCategoryByName(name) {
      const n = (name || '').trim()
      if (!n) return null
      return (this.projectCategoryOptions || []).find(c => c.categoryName === n) || null
    },
    async loadProtocolOptions() {
      try {
        const res = await gasApi.protocol.list({ pageNum: 1, pageSize: 500 })
        this.protocolOptions = (res.data || {}).records || []
      } catch (e) {
        this.protocolOptions = []
      }
    },
    protocolOptionLabel(p) {
      const code = p.protocolCode ? ` (${p.protocolCode})` : ''
      return `${p.protocolName || ''}${code}`
    },
    protocolMatchText(p) {
      return [
        p.protocolName,
        p.protocolCode,
        p.commType,
        p.applyMode,
        p.version,
        p.description
      ].filter(Boolean).join(' ').toLowerCase()
    },
    filterProtocolOption(query) {
      this.protocolFilterQuery = query
    },
    onProtocolDropdownVisible(open) {
      if (!open) this.protocolFilterQuery = ''
    },
    onMapPickConfirm({ longitude, latitude }) {
      this.form.longitude = longitude
      this.form.latitude = latitude
      this.$message.success('已更新经纬度')
    },
    refreshAll() {
      this.loadStats()
      this.loadModelOptions()
      this.loadDeviceTypeOptions()
      this.loadSiteOptions()
      this.loadProjectCategoryOptions()
      this.loadData()
    },
    search() {
      this.query.pageNum = 1
      this.refreshAll()
    },
    resetSearch() {
      this.advancedVisible = false
      this.query = {
        pageNum: 1,
        pageSize: this.query.pageSize,
        deviceName: '',
        terminalType: '',
        projectCategoryId: null,
        deviceCode: '',
        siteName: '',
        orgId: null,
        model: '',
        enabledStatus: ''
      }
      this.refreshAll()
    },
    rowNo(index) {
      return (this.query.pageNum - 1) * this.query.pageSize + index + 1
    },
    formatLngLat(row) {
      if (row.longitude == null && row.latitude == null) return '—'
      const lng = row.longitude != null ? row.longitude : '—'
      const lat = row.latitude != null ? row.latitude : '—'
      return `${lng} / ${lat}`
    },
    openView(row) {
      this.viewRow = { ...row }
      this.viewVisible = true
      if (!(this.protocolOptions || []).length) this.loadProtocolOptions()
    },
    openDetailDrawer(row) {
      this.drawerDeviceId = row.deviceId
      this.drawerVisible = true
    },
    pushDevice(row) {
      this.pushDeviceRow = { ...row }
      this.pushDrawerVisible = true
    },
    async batchStatus(enabledStatus) {
      if (!this.selectedIds.length) return this.$message.warning('请先选择设备')
      await gasApi.device.batchStatus({ ids: this.selectedIds, enabledStatus })
      this.$message.success('操作成功')
      this.refreshAll()
    },
    afterBatchRemove() {
      this.refreshAll()
    },
    async removeRow(row) {
      await this.$confirm('删除后设备将进入回收站，设备监控、统计、趋势图、通信报文、报表和告警中都不再显示该设备。可在回收站彻底删除。', '移入回收站', { type: 'warning' })
      await this.removeApi(row[this.idField])
      this.$message.success('已移入回收站')
      this.refreshAll()
    },
    triggerImport() {
      this.$refs.importInput && this.$refs.importInput.click()
    },
    async onImportFile(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      const text = await file.text()
      const lines = text.split(/\r?\n/).filter(l => l.trim())
      if (lines.length < 2) return this.$message.warning('CSV 至少需要表头与一行数据')
      if (!this.projectCategoryOptions.length) await this.loadProjectCategoryOptions()
      const headers = lines[0].split(',').map(h => h.trim())
      let ok = 0
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',')
        const row = {}
        headers.forEach((h, idx) => { row[h] = (cols[idx] || '').trim() })
        if (!row.deviceName || !row.deviceCode) continue
        const categoryName = row.projectCategoryName || row.projectCategory || row['项目分类'] || ''
        const category = this.findProjectCategoryByName(categoryName)
        try {
          await gasApi.device.save({
            deviceName: row.deviceName,
            deviceCode: row.deviceCode,
            siteName: row.siteName || '',
            projectCategoryId: category ? category.categoryId : null,
            projectCategoryName: category ? category.categoryName : categoryName,
            model: row.model || '',
            ip: row.ip || '',
            port: Number(row.port || 0) || null,
            protocolType: row.protocolType || '',
            enabledStatus: row.enabledStatus === '1' ? '1' : '0',
            terminalStatus: row.terminalStatus || 'offline'
          })
          ok++
        } catch (err) {
          /* skip duplicate */
        }
      }
      this.$message.success(`导入完成，成功 ${ok} 条`)
      this.refreshAll()
    },
    async exportData() {
      const res = await gasApi.device.list({ ...this.buildQuery(), pageNum: 1, pageSize: 10000 })
      const rows = (res.data || {}).records || []
      const header = ['站点名称', '设备名称', '设备编号', '项目分类', '型号', '启用状态', '协议类型', '客户', 'IP', '端口', '终端状态', '更新时间']
      const csv = [header.join(',')]
      rows.forEach(r => {
        csv.push([
          r.siteName, r.deviceName, r.deviceCode, r.projectCategoryName, r.model,
          r.enabledStatus === '0' ? '启用' : '停用',
          r.protocolType, r.customerName, r.ip, r.port, r.terminalStatus, r.updateTime
        ].map(v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`).join(','))
      })
      const blob = new Blob(['\ufeff' + csv.join('\n')], { type: 'text/csv;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = '设备列表.csv'
      link.click()
      URL.revokeObjectURL(link.href)
      this.$message.success('导出成功')
    },
    async doSubmitForm() {
      if (!this.form.deviceName || !this.form.deviceCode) return this.$message.warning('请填写设备名称和编号')
      const payload = { ...this.form }
      this.onProjectCategoryChange(payload.projectCategoryId)
      payload.projectCategoryName = this.form.projectCategoryName || ''
      if (!payload.deviceId && !payload.terminalStatus) {
        payload.terminalStatus = 'offline'
      }
      await this.saveApi(payload)
      this.$message.success('保存成功')
      this.dialogVisible = false
      this.refreshAll()
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/report-flow.scss';

.stat-row {
  grid-template-columns: repeat(5, 1fr);
  margin-bottom: 16px;
}
@media (max-width: 1200px) {
  .stat-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

.search-section {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  padding: 16px 16px 4px;
  margin-bottom: 16px;
}
.section-title {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  margin-bottom: 12px;
}
.title-bar {
  width: 3px;
  height: 14px;
  background: #1677ff;
  border-radius: 2px;
  margin-right: 8px;
}
.search-form-layout {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
}
.search-form-layout >>> .el-form-item {
  margin-bottom: 12px;
  margin-right: 0;
}
.search-actions {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  margin-bottom: 12px;
  white-space: nowrap;
}
.search-actions .el-button + .el-button {
  margin-left: 8px;
}
.btn-advanced {
  padding-left: 0;
  margin-right: 4px;
}

.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}
.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.btn-enable {
  color: #00b42a;
  border-color: #00b42a;
}
.btn-disable {
  color: #86909c;
  border-color: #c9cdd4;
}
.btn-del {
  color: #f53f3f;
  border-color: #f53f3f;
}
.hidden-file {
  display: none;
}

.device-name {
  color: #1677ff;
  font-weight: 500;
  line-height: 1.4;
}
.device-name.link {
  cursor: pointer;
}
.device-name.link:hover {
  text-decoration: underline;
}
.device-code {
  color: #86909c;
  font-size: 12px;
}
.op-link {
  color: #1677ff;
  padding: 0 2px;
}
.op-del {
  color: #f53f3f;
  padding: 0 2px;
}
.map-pick-hint {
  margin-left: 10px;
  font-size: 12px;
  color: #86909c;
}
</style>
